<template>
  <div :class="{'content-box': !fullSizable}">
    <LabelComponent v-if="!fullSizable"
                    class="gantt-chart-label"
                    :label_text="drawData.meta.parents.join(' -> ')"
                    label_type="headline/h2/bold 18"
    />
    <div :id="this.chartNumber" class="gantt-chart-root" :style="{
        'width': this.maxChartWidth + 'px',
        'height': this.maxDiagramHeight + 'px',
        'min-width': this.maxChartWidth + 'px',
        'min-height': this.maxDiagramHeight + 'px',
        'max-width': this.maxChartWidth + 'px',
        'max-height': this.maxDiagramHeight + 'px',
      }">
    </div>
  </div>
</template>

<script type="text/javascript">
import gestures from '@/assets/gestures.js';
import LabelComponent from '@/units/RichLabel.vue';

class DateMulter {
  constructor(view, mult) {
    this.view = view;
    this.strenghtMult = mult;
    this.mult = mult == 0 ? 1 : 1 / mult;
  }

  normaliseDate(rawDate) {
    return rawDate;
  }

  addPeriod(workDate, count) {
    // workDate.setMonth(workDate.getMonth() + count);
  }

  getDateView(workDate) {
    return '-';
  }

  checkAddToRuler(checkDate, idx, clearer) {
    return idx % clearer == 0;
  }

  getRulerElems(basedSteps, sTimestamp, eTimestamp) {
    basedSteps += 2;

    let ans = [];
    const lastDate = this.normaliseDate(Date.fromHoursTime(sTimestamp));
    this.addPeriod(lastDate, -1);
    const endDate = this.normaliseDate(Date.fromHoursTime(eTimestamp));
    this.addPeriod(endDate, 1);

    while (endDate > lastDate) {
      const view = this.getDateView(lastDate);

      ans.push({
        view: view,
        date: new Date(lastDate),
        time: lastDate.getHoursTime(),
      });

      this.addPeriod(lastDate, 1);
    }

    const clearer = Math.max(Math.floor(ans.length / basedSteps), 1);
    ans = ans.filter((elem, idx) => this.checkAddToRuler(elem.date, idx, clearer));

    return ans;
  }
}

class DayMulter extends DateMulter {
  normaliseDate(rawDate) {
    return new Date(rawDate.getFullYear(), rawDate.getMonth(), rawDate.getDate());
  }

  addPeriod(workDate, count) {
    workDate.setDate(workDate.getDate() + count);
  }

  getDateView(workDate) {
    return String(workDate.getDate()).padStart(2, '0') + ' ' + workDate.toLocaleString('default', {month: 'long'});
  }

  checkAddToRuler(checkDate, idx, clearer) {
    const curDate = checkDate.getDate();
    const datesClearer = clearer % 30;

    if (curDate == 1) return true;
    if (datesClearer <= 1) return true;
    if ([28, 29, 30, 31].includes(curDate)) return false;

    if (datesClearer == 2) return (curDate-1) % 2 == 0;
    if (datesClearer > 2 && datesClearer <= 5) return curDate % 5 == 0;
    if (datesClearer > 5 && datesClearer <= 10) return curDate % 10 == 0;
    return curDate % 15 == 0;
  }
}

class MonthMulter extends DateMulter {
  normaliseDate(rawDate) {
    return new Date(rawDate.getFullYear(), rawDate.getMonth(), 1);
  }

  addPeriod(workDate, count) {
    workDate.setMonth(workDate.getMonth() + count);
  }

  getDateView(workDate) {
    return workDate.toLocaleString('default', {month: 'long'}) + ' ' + workDate.toLocaleString('default', {year: 'numeric'});
  }

  checkAddToRuler(checkDate, idx, clearer) {
    const curDate = checkDate.getMonth();
    const datesClearer = clearer % 12;

    if (curDate == 0) return true;
    if (datesClearer <= 1) return true;
    if (datesClearer == 2) return curDate % 2 == 0;
    if (datesClearer == 3) return curDate % 3 == 0;
    return curDate % 6 == 0;
  }
}

class YearMulter extends DateMulter {
  normaliseDate(rawDate) {
    return new Date(rawDate.getFullYear(), 1, 1);
  }

  addPeriod(workDate, count) {
    workDate.setYear(workDate.getFullYear() + count);
  }

  getDateView(workDate) {
    return workDate.toLocaleString('default', {year: 'numeric'});
  }
}

const selfComponent = {
  components: {
    LabelComponent,
  },
  mounted() {
    this.clearOldChart();
    this.drawChart();
  },
  unmounted() {
    this.clearOldChart();
  },
  watch: {
    drawData() {
      this.clearOldChart();
      this.drawChart();
    },
  },
  data: () => ({
    // eslint-disable-next-line new-cap
    chartNumber: 'chart_' + Number.UNIQUE(),
    gestController: null,
    chartPainter: null,
  }),
  computed: {

  },
  methods: {
    clearOldChart() {
      if (this.gestController) {
        this.gestController.destruct();
        this.gestController = null;
      }
      if (this.chartPainter) {
        this.chartPainter.dispose();
        this.chartPainter = null;
      }
    },
    getMultiplier(multData) {
      const ans = this.hourMultipliers.filter((ms) => multData >= ms.strenghtMult).pop();
      return ans ? ans : this.hourMultipliers[0];
    },
    drawChart() {
      if (this.drawData == null) return;

      let rootContainer = null;

      // create stage
      const root = acgraph.create(this.chartNumber);
      this.chartPainter = root;

      root.suspend();

      // get bounds of stage
      const rootBounds = root.getBounds();

      // console.log('rootBounds', rootBounds, BASED_DATA);

      // calculate chip width/height
      const rootW = rootBounds.width;
      const rootH = rootBounds.height;

      const basedSide = Math.min(rootW, rootH, 300);

      // console.log('basedSide', basedSide);

      const rulerW = basedSide / 6;
      const legendRulerW = rulerW * 3;

      const mainRulerView = root.layer();
      const legendRulerView = root.layer();
      // var subRulerLayer = root.layer();
      const chartView = root.layer();
      const popupLayer = root.layer();

      const chartViewW = rootW - legendRulerW;

      mainRulerView.setPosition(legendRulerW, 0);
      mainRulerView.clip({left: 0, top: 0, height: rootH, width: chartViewW});
      // mainRulerView.rect(-10000, -10000, 20000, 20000).fill('#00f');

      // legendRulerView.setPosition(0, 0);
      legendRulerView.clip({left: 0, top: rulerW, height: rootH, width: rootW});
      // legendRulerView.rect(-10000, -10000, 20000, 20000).fill('#f00');

      // legendRulerView.scale(0.5, 0.5, legendRulerW, rootH);

      // chartView.rotate(-90, legendRulerW, rulerW);
      chartView.setPosition(legendRulerW, 0);

      chartView.clip({left: 0, top: rulerW, height: rootH - rulerW, width: chartViewW});
      // chartView.rect(-10000, -10000, 20000, 20000).fill('#0f0');

      // chartView.rect(50, 50, -100, 100).fill('#0ff');

      const chartUserTranformer = chartView.layer();

      // const zoomCircle = chartView.circle(50, 50, 4).fill('red');

      const chartLayer = chartUserTranformer.layer();
      const mainRulerLayer = mainRulerView.layer();
      const legendRulerLayer = legendRulerView.layer();
      // legendRulerLayer.translate(0, 100);
      // console.log('legendRulerLayer', legendRulerLayer.getY());
      legendRulerLayer.rect(0, 0, 0, 0);
      // legendRulerLayer.rect(0, -100, 0, 0);
      // console.log('legendRulerLayer', legendRulerLayer.getY());

      // chartLayer.setPosition(0, 0);
      // chartLayer.rect(0, 0, 0, 0);

      const legendStep = basedSide / 7;
      const candleW = legendStep / 2;

      const mainRulerElemW = basedSide / 4;

      let minVal = Infinity;
      let maxVal = -Infinity;

      const minTimestamp = (new Date(0)).getHoursTime();

      if (this.fullSizable) {
        const fullSizer = root.image('/icons/system/arrow-in-full-size.svg', basedSide / 30, basedSide / 30, basedSide / 8, basedSide / 8);

        acgraph.events.listen(fullSizer, ['mousedown', 'touchstart'], (e) => {
          this.$emit('open_full_size');
        });
      }

      // console.log(this, this.component, selfComponent);

      const padding = basedSide / 35;
      const gap = basedSide / 19;
      popupLayer.rect(0, 0, basedSide, 2 * padding + 5 * gap).fill('#ffffff');
      const mainPopupText = popupLayer.text(padding, padding, '', {
        width: basedSide - padding * 2,
        height: gap,
        textOverflow: '...',
      });
      const fromPopupText = popupLayer.text(padding, padding + gap, '');
      const toPopupText = popupLayer.text(padding, padding + 2 * gap, '');
      const parentPopupText = popupLayer.text(padding, padding + 3 * gap, '', {
        width: basedSide / 1.5,
        wordBreak: 'normal',
      });
      popupLayer.visible(false);

      function updatePopup(popupData, forPopupBounds) {
        popupLayer.visible(true);
        mainPopupText.text('Дело: ' + popupData.view);
        fromPopupText.text('С: ' + new Date(popupData.s).toLocaleString());
        toPopupText.text('По: ' + new Date(popupData.e).toLocaleString());
        parentPopupText.text('В составе: ' + String(popupData.parent.parent));

        popupLayer.setPosition(forPopupBounds.left + forPopupBounds.width / 2, forPopupBounds.top + forPopupBounds.height);

        // console.log('forPopupBounds', forPopupBounds);
      }

      // console.log('this.activeGroup', this.activeGroup);

      this.drawData.data.forEach((row, idx) => {
        const legendY = rulerW + idx * legendStep;

        legendRulerLayer.text(0, legendY, row.parent, {
          width: legendRulerW,
          height: legendStep,
          fontSize: legendRulerW / 12,
          textOverflow: '...',
          vAlign: 'middle',
          wordBreak: 'normal',
        }); // .domElement().className = 'gantt_chart_text';

        legendRulerLayer.rect(legendRulerW, legendY + legendStep/2, rootW, 1).stroke(null, 0).fill('gray');

        row.data.forEach((dataElem) => {
          dataElem.parent = row;
          const sTime = (new Date(dataElem.s)).getHoursTime();
          const eTime = (new Date(dataElem.e)).getHoursTime();

          if (minTimestamp > sTime || minTimestamp > eTime || eTime-sTime < 1) {
            return;
          }

          minVal = Math.min(sTime, eTime, minVal);
          maxVal = Math.max(sTime, eTime, maxVal);
          const dataView = chartLayer.rect(sTime, legendY + legendStep/2 - candleW / 2, eTime-sTime, candleW).fill(dataElem.color).stroke(dataElem.color, 1);

          acgraph.events.listen(dataView, ['mousedown', 'touchstart'], (e) => {
            e.preventDefault();
            updatePopup(dataElem, dataView.getAbsoluteBounds());
          });
        });
      });

      chartLayer.rect(minVal, 0, 0, 0);

      let scaleFactor = 1;

      const userTransform = {
        x: 0,
        y: 0,
        sx: 1,
        sy: 1,
      };

      const timeRangesCount = Math.floor(chartViewW / mainRulerElemW);

      root.resume();

      const recalcTransform = (translation, scale, init) => {
        root.suspend();
        scaleFactor *= scale.factor;

        if (!init) {
          userTransform.x += translation.x;
          userTransform.y += translation.y;
          userTransform.sy *= scale.factor;

          const oldChartMatrix = chartUserTranformer.getTransformationMatrix();

          chartUserTranformer.scale(scale.factor, 1, scale.x, scale.y);

          const newChartMatrix = chartUserTranformer.getTransformationMatrix();

          userTransform.x += newChartMatrix[4] - oldChartMatrix[4];
        } else {
          chartLayer.setPosition(0, 0);
          chartLayer.scale(scale.factor, 1, scale.x, scale.y);
        }

        chartUserTranformer.setPosition(userTransform.x, userTransform.y);
        legendRulerLayer.setPosition(0, userTransform.y);

        const multer = this.getMultiplier(mainRulerElemW / scaleFactor);

        const newMin = (minVal * scaleFactor - userTransform.x) / scaleFactor;
        const newMax = (minVal * scaleFactor - userTransform.x + chartViewW) / scaleFactor;

        const rulerElems = multer.getRulerElems(timeRangesCount, newMin, newMax);

        mainRulerLayer.removeChildren();

        const fontSize = legendRulerW / 12;

        rulerElems.forEach((elem, idx) => {
          mainRulerLayer.rect((elem.time - newMin) * scaleFactor, rulerW, 1, rootH).fill('gray').stroke(null, 0);

          const tPos = {
            x: (elem.time - newMin) * scaleFactor,
            y: 0,
          };

          const curText = mainRulerLayer.text(tPos.x, tPos.y, elem.view, {
            height: rulerW,
            fontSize: fontSize,
            textOverflow: '...',
            wordBreak: 'normal',
          }); // .domElement().className = 'gantt_chart_text';

          curText.rotate(-45, tPos.x, tPos.y);
          curText.setPosition(curText.getX(), curText.getY() + rulerW - fontSize);
        });
        root.resume();
      };

      recalcTransform({
        x: -minVal,
        y: 0,
      }, {factor: chartViewW / (maxVal - minVal), x: 0, y: 0}, true);

      function dragController(moveDistance, event) {
        if (event.target.tagName != 'path') popupLayer.visible(false);
        recalcTransform(moveDistance, {factor: 1, x: 0, y: 0});
      }

      // Matrix.transpose * (Matrix * Matrix.transpose).inverse

      function multiplyMatrix(mat1, mat2) {
        return {
          // eslint-disable-next-line max-len
          a: mat1.a*mat2.a + mat1.c*mat2.b + mat1.e * 0, c: mat1.a*mat2.c + mat1.c*mat2.d + mat1.e * 0, e: mat1.a*mat2.e + mat1.c*mat2.f + mat1.e * 1,
          // eslint-disable-next-line max-len
          b: mat1.b*mat2.a + mat1.d*mat2.b + mat1.f * 0, d: mat1.b*mat2.c + mat1.d*mat2.d + mat1.f * 0, f: mat1.b*mat2.e + mat1.d*mat2.f + mat1.f * 1, /*
          // eslint-disable-next-line max-len
          0                                              0                                              1                                              */
        };
      }

      function normaliseMatrix(rawMat) {
        return {
          a: rawMat[0], c: rawMat[2], e: rawMat[4],
          b: rawMat[1], d: rawMat[3], f: rawMat[5],
        };
      }

      function matrixInverse(mat) {
        const detM = mat.a*mat.d - mat.c*mat.b;
        // this.m11_ / detM, -this.m10_ / detM, -this.m01_ / detM, this.m00_ / detM, (this.m01_ * this.m12_ - this.m11_ * this.m02_) / detM, (this.m10_ * this.m02_ - this.m00_ * this.m12_) / detM

        return {
          a: mat.d / detM, c: -mat.b / detM, e: (mat.b * mat.f - mat.d * mat.e) / detM,
          b: -mat.b / detM, d: mat.a / detM, f: (mat.b * mat.e - mat.a * mat.f) / detM,
        };
      }

      function applyMatrix(tMatrix, x, y) {
        return {
          x: tMatrix.a * x + tMatrix.c * y + tMatrix.e,
          y: tMatrix.b * x + tMatrix.d * y + tMatrix.f,
        };
      }

      function zoomController(zoomData) {
        const diagramRect = rootContainer.getBoundingClientRect();

        function getFullMatrix(elem, startMat) {
          startMat = startMat ? startMat : normaliseMatrix([1, 0, 0, 1, 0, 0]);
          if (elem == null || !elem.hasParent || !elem.hasParent()) return startMat;

          // console.log('elem.getTransformationMatrix()', elem.getTransformationMatrix());

          startMat = multiplyMatrix(normaliseMatrix(elem.getTransformationMatrix()), startMat);
          return getFullMatrix(elem.parent(), startMat);
        }
        // const localToGlobalMatrix = getFullMatrix(chartUserTranformer);
        const globalToLocalMatrix = matrixInverse(getFullMatrix(chartView));
        // fillParentList(chartUserTranformer, parents);

        // const transformMatrix = chartUserTranformer.getTransformationMatrix();

        // console.log('transformMatrix', transformMatrix);

        const centerX = (zoomData.pX - diagramRect.left);
        const centerY = (zoomData.pY - diagramRect.top);

        const pos = applyMatrix(globalToLocalMatrix, centerX, centerY);

        // zoomCircle.setPosition(pos.x, pos.y);

        // const change = zoomData.sizeX * 0.1 * scaleFactor;

        const zoom = {factor: 1 - zoomData.sizeX * 0.1, x: pos.x, y: pos.y};

        // console.log('zoom', scaleFactor);

        recalcTransform({x: 0, y: 0}, zoom);
      }

      // document.addEventListener('DOMContentLoaded', () => {
      rootContainer = document.getElementById(this.chartNumber);

      // rootContainer.addEventListener('click', (e) => {
      //   // console.log('event', e);
      //   if (e.target.tagName == 'svg') popupLayer.visible(false);
      // });

      this.gestController = gestures(rootContainer);
      this.gestController.registerDrag(dragController);
      this.gestController.registerZoom(zoomController);
      // });
    },
  },
  props: {
    fullSizable: {
      default: true,
    },
    maxChartWidth: {
      default: 350,
      required: false,
    },
    maxDiagramHeight: {
      default: 250,
      required: false,
    },
    isVertical: {
      default: true,
    },
    drawData: {
      default: null,
    },
    hourMultipliers: {
      default: [
        // new DateMulter(
        //     'миллисекунда',
        //     1 / 360000,
        // ),
        // new DateMulter(
        //     'секунда',
        //     1 / 360,
        // ),
        // new DateMulter(
        //     'минута',
        //     1 / 60,
        // ),
        // new DateMulter(
        //     'час',
        //     1,
        // ),
        new DayMulter(
            'день',
            24,
        ),
        new MonthMulter(
            'месяц',
            24 * 30,
        ),
        new YearMulter(
            'год',
            24 * 365,
        ),
        // new DateMulter(
        //     '8 лет',
        //     24 * (366 * 2 + 365 * 6),
        // ),
        // new DateMulter(
        //     '16 лет',
        //     24 * (366 * 2 + 365 * 6) * 2,
        // ),
        // new DateMulter(
        //     '24 года',
        //     24 * (366 * 2 + 365 * 6) * 3,
        // ),
        // new DateMulter(
        //     '32 года',
        //     24 * (366 * 2 + 365 * 6) * 4,
        // ),
        // new DateMulter(
        //     '40 лет',
        //     24 * (366 * 2 + 365 * 6) * 5,
        // ),
      ],
    },
  },
};

export default selfComponent;
</script>

<style lang="less">

.gantt-chart-label {
  // margin-top: 27px;
  margin-bottom: 13px;
}

.gantt-chart-root tspan {
  fill: @no-accent-text-color;
}

</style>
